// KpiBar — patrón global (§6.1 de la arquitectura): fila densa de KPIs bajo el intro de la
// pantalla (`.kpi-bar` del mock). Cada item: label uppercase + valor tabular + sub opcional.
// Lo usan los homes y pantallas de análisis/config de todos los portales.
import type { ReactNode } from 'react';
import { HelpHint } from '@/components/ui/HelpHint';
import { cn } from '@/lib/cn';

export type KpiTone = 'default' | 'success' | 'warning' | 'info' | 'destructive';

export interface KpiItem {
    label: string;
    value: ReactNode;
    sub?: ReactNode;
    tone?: KpiTone;
    /** Lo que la celda explica al pasar el puntero — p. ej. sobre qué universo cuenta el número. */
    title?: string;
    /** Qué mide el indicador; va en su «?». */
    help?: string;
}

const TONE_CLASS: Record<KpiTone, string> = {
    default: 'text-foreground',
    success: 'text-success',
    warning: 'text-warning',
    info: 'text-info',
    destructive: 'text-destructive',
};

export function KpiBar({ items, className }: { items: KpiItem[]; className?: string }) {
    return (
        <div
            className={cn(
                'flex items-center overflow-x-auto border-b border-border bg-card px-3 py-2',
                className,
            )}
        >
            {items.map((item) => (
                <KpiCell key={item.label} item={item} />
            ))}
        </div>
    );
}

/**
 * Una celda suelta de la barra (`.kpi` del mock). Se exporta para la pantalla que ya pone su propia
 * franja —una `<ScreenBand>`, que trae las mismas clases que el contenedor de arriba—: anidarlas
 * duplicaría el padding y el borde, así que esa pantalla mapea las celdas ella misma.
 */
export function KpiCell({ item }: { item: KpiItem }) {
    return (
        <div
            title={item.title}
            className="flex flex-none items-baseline gap-1.5 border-r border-border px-3.5 last:border-r-0"
        >
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                {item.label}
            </span>
            {item.help ? (
                <HelpHint text={item.help} label={`Ayuda: ${item.label}`} className="self-center" />
            ) : null}
            <span
                className={cn(
                    'text-sm font-semibold tabular-nums',
                    TONE_CLASS[item.tone ?? 'default'],
                )}
            >
                {item.value}
            </span>
            {item.sub ? (
                <span className="text-[10px] text-muted-foreground">{item.sub}</span>
            ) : null}
        </div>
    );
}
